import React, { useEffect, useMemo, useRef, useState } from "react";
import { getRouteApi } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
// Mightier Survey Engine
import {
  SurveyEngineIface,
  useSurveyEngine,
} from "src/services/surveyHooks/useSurveyEngine";
import { useSurveyNavigationV2 } from "src/services/surveyHooks/survey-navigation";
import {
  ANSWER_TYPE_MAP,
  Question,
} from "@neuromotion-inc/mightier-survey-engine";
// Components
import { PageHeader } from "@components/page-header/page-header";
import { SurveyTextInput } from "@components/survey-components/survey-text-input/survey-text-input";
import { SurveySelectInput } from "@components/survey-components/survey-select-input/survey-select-input";
import { SurveyBooleanInput } from "@components/survey-components/survey-boolean-input/survey-boolean-input";
import { SurveyNumberInput } from "@components/survey-components/survey-number-input/survey-number-input";
import { SurveyUiSection } from "@components/survey-components/survey-ui-section/survey-ui-section";
// alt UI (start and ending)
import {
  SurveyFlowStartUI,
  SurveyFlowEndUI,
} from "./survey-flow-extra-views/survey-flow-extra-views";
// Styling
import { SurveyQuestionText } from "@components/survey-components/survey-question-text/survey-question-text";
import {
  Grid,
  Group,
  Container,
  Button,
  ActionIcon,
  LoadingOverlay,
} from "@mantine/core";
import { IconChevronUp, IconChevronDown } from "@tabler/icons-react";
import {
  validateTextInput,
  validateNumberInput,
  validateChoicesInput,
  validateBooleanInput,
} from "@utils/validation";
import { MobileNavigationButton } from "@components/survey-components/mobile-navigation-button/mobile-navigation-button";
import { useSurveyStateStore } from "src/services/surveyHooks/survey-state-local-store";
import { getSessionUuid } from "@utils/sessionUtils";

import { useTrackingQuestionsViewed } from "src/services/surveyHooks/survey-analytics";

const validationFunctions = {
  TEXT: validateTextInput,
  NUMBER: validateNumberInput,
  CHOICES: validateChoicesInput,
  BOOLEAN: validateBooleanInput,
} as const;

const route = getRouteApi("/s/$surveyId");

export const SurveyFlowPage = React.memo(function SurveyFlowPage() {
  const { surveyId } = route.useParams();
  const { meta } = route.useSearch();

  const [hasDismissedStartPage, setHasDismissedStartPage] = useState(false);
  const [shouldShowEndingPage, setShouldShowEndingPage] = useState(false);

  const [isPendingSubmit, setIsPendingSubmit] = useState(false);

  const { t: mightierSurveyT } = useTranslation("mightier_survey_lib");
  const { t } = useTranslation();

  const { saveSurveyState, deleteSurveyState } = useSurveyStateStore();

  const surveyEngine = useSurveyEngine(surveyId, meta);

  const { sendAnalyticInBackground } = surveyEngine;

  const {
    currentStepIndex,
    currentStep,
    goToPreviousStep,
    goToNextStep,
    isOnLastStep,
  } = useSurveyNavigationV2(surveyEngine);

  // Logic for scrolling to top of next question
  const surveyContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (surveyContainerRef.current) {
      surveyContainerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [currentStepIndex]);

  // hook to track questionViewed whenever question changes
  useTrackingQuestionsViewed(
    currentStep,
    surveyEngine.eventBuilder,
    sendAnalyticInBackground,
  );

  const isMobile = window.innerWidth <= 600;

  const stepsLength = surveyEngine.stepsAndQuestionsToDisplay.length;

  const questionIdToValidationErrorsMap = useMemo(() => {
    const map = new Map();

    for (let i = 0; i < stepsLength; i++) {
      const step = surveyEngine.getStepByIdx(i);
      const questions = step.sections
        .filter((s) => s.type === "QUESTION")
        .map((s) => s.content);

      for (const question of questions) {
        const response = surveyEngine.getResponseForQuestion(question.id);
        let errors: string[] = [];

        if (
          question.type === "TEXT" &&
          response?.type === ANSWER_TYPE_MAP[question.type]
        ) {
          const validateFunction =
            validationFunctions[ANSWER_TYPE_MAP[question.type]];
          errors = validateFunction(response, question.validate, t);
        }

        if (
          question.type === "NUMBER" &&
          response?.type === ANSWER_TYPE_MAP[question.type]
        ) {
          const validateFunction =
            validationFunctions[ANSWER_TYPE_MAP[question.type]];
          errors = validateFunction(response, question.validate, t);
        }

        if (
          (question.type === "SINGLE_SELECT" ||
            question.type === "MULTI_SELECT") &&
          response?.type === ANSWER_TYPE_MAP[question.type]
        ) {
          const validateFunction =
            validationFunctions[ANSWER_TYPE_MAP[question.type]];
          errors = validateFunction(response, question.validate, t);
        }

        if (
          question.type === "BOOLEAN" &&
          response?.type === ANSWER_TYPE_MAP[question.type]
        ) {
          const validateFunction =
            validationFunctions[ANSWER_TYPE_MAP[question.type]];
          errors = validateFunction(response, question.validate, t);
        }

        map.set(question.id, errors);
      }
    }

    return map;
  }, [surveyEngine, stepsLength, t]);

  const disabledSubmit = useMemo(() => {
    const questions = currentStep.sections
      .filter((s) => s.type === "QUESTION")
      .map((s) => s.content);
    return questions.some((q: Question) => {
      // Check if a required question is unanswered
      const response = surveyEngine.getResponseForQuestion(q.id);
      const isRequiredQuestionUnanswered = q.validate?.required && !response;

      // Check if there are validation errors with the responses
      const errors = questionIdToValidationErrorsMap.get(q.id);
      const hasErrors = errors && errors.length >= 1;

      // If the question is required and unanswered, or has errors then submit button is disabled
      return isRequiredQuestionUnanswered || hasErrors;
    });
  }, [currentStep.sections, questionIdToValidationErrorsMap, surveyEngine]);

  //
  //
  const handleStepSubmit = async (
    currentStepId: string,
    surveyEngine: SurveyEngineIface,
  ): Promise<void> => {
    setIsPendingSubmit(true);
    try {
      await surveyEngine.submitStep(currentStepId, {
        sessionId: getSessionUuid(),
      });
    } finally {
      setIsPendingSubmit(false);
    }

    saveSurveyState(surveyEngine);

    if (isOnLastStep) {
      setShouldShowEndingPage(true);
      //
      sendAnalyticInBackground({
        eventPayload: surveyEngine.eventBuilder.buildInstanceCompletedEvent(),
      });
      // clear local storage after completion, so when the user returns they start from the beginning
      deleteSurveyState(
        surveyEngine.surveyConfigId,
        surveyEngine.surveyConfigVersion,
      );
    } else {
      goToNextStep();
    }
  };

  //
  if (!surveyEngine.hasBeenStarted && !hasDismissedStartPage) {
    return (
      <SurveyFlowStartUI
        surveyEngine={surveyEngine}
        onPressStart={() => {
          setHasDismissedStartPage(true);
        }}
        startPageConfig={surveyEngine.startPageConfig}
      />
    );
  }

  if (shouldShowEndingPage) {
    return (
      <SurveyFlowEndUI
        surveyEngine={surveyEngine}
        endPageConfig={surveyEngine.endingPageConfig}
      />
    );
  }

  return (
    <div ref={surveyContainerRef}>
      {!isMobile ? <PageHeader /> : null}

      {isPendingSubmit ? (
        <LoadingOverlay
          visible
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 3 }}
        />
      ) : null}

      <Container mt="xl" mb="xl">
        <Grid p="sm" gutter="xl">
          {/* Iterate through each question in the current step and render the correct question type */}
          {currentStep.sections.map((section) => {
            /**
             * NOTE: can't just use `index` as key because after submitting step, currentStep.sections
             * changes, but react does not re-render sometimes if key is the same.
             *
             * So we use section.id as the key which is a UUID, i.e. always unique
             */
            const key = section.id;

            if (section.type !== "QUESTION") {
              return (
                <Grid.Col span={12} key={key}>
                  <SurveyUiSection
                    section={section.content}
                    surveyEngine={surveyEngine}
                  />
                </Grid.Col>
              );
            }

            const question = section.content;

            const testId = `survey-${question.type.toLowerCase()}-${question.id}`;

            if (question.type === "TEXT") {
              return (
                <Grid.Col span={12} key={key}>
                  <SurveyQuestionText
                    required={question.validate?.required}
                    questionText={question.question_text}
                    surveyEngine={surveyEngine}
                  />
                  <SurveyTextInput
                    question={question}
                    surveyEngine={surveyEngine}
                    questionIdToValidationErrorsMap={
                      questionIdToValidationErrorsMap
                    }
                    testId={testId}
                  />
                </Grid.Col>
              );
            }

            if (question.type === "NUMBER") {
              return (
                <Grid.Col span={12} key={key}>
                  <SurveyQuestionText
                    required={question.validate?.required}
                    questionText={question.question_text}
                    surveyEngine={surveyEngine}
                  />
                  <SurveyNumberInput
                    question={question}
                    surveyEngine={surveyEngine}
                    questionIdToValidationErrorsMap={
                      questionIdToValidationErrorsMap
                    }
                    testId={testId}
                  />
                </Grid.Col>
              );
            }

            if (question.type === "BOOLEAN") {
              return (
                <Grid.Col span={12} key={key}>
                  <SurveyQuestionText
                    required={question.validate?.required}
                    questionText={question.question_text}
                    surveyEngine={surveyEngine}
                  />
                  <SurveyBooleanInput
                    question={question}
                    surveyEngine={surveyEngine}
                    questionIdToValidationErrorsMap={
                      questionIdToValidationErrorsMap
                    }
                    testId={testId}
                  />
                </Grid.Col>
              );
            }

            if (
              question.type === "MULTI_SELECT" ||
              question.type === "SINGLE_SELECT"
            ) {
              return (
                <Grid.Col span={12} key={key}>
                  <SurveyQuestionText
                    required={question.validate?.required}
                    questionText={question.question_text}
                    surveyEngine={surveyEngine}
                  />
                  <SurveySelectInput
                    question={question}
                    surveyEngine={surveyEngine}
                    questionIdToValidationErrorsMap={
                      questionIdToValidationErrorsMap
                    }
                    testId={`${testId}`}
                  />
                </Grid.Col>
              );
            }

            return null;
          })}
          {!isMobile ? (
            <Grid.Col span={12}>
              <Button
                onClick={() => handleStepSubmit(currentStep.id, surveyEngine)}
                disabled={disabledSubmit}
                data-testid="survey-submit-button"
              >
                {mightierSurveyT(
                  currentStep.submit_button.text_translation_key,
                )}
              </Button>
            </Grid.Col>
          ) : null}
        </Grid>
        {isMobile ? (
          <MobileNavigationButton
            currentStepIndex={currentStepIndex}
            goToPreviousStep={goToPreviousStep}
            disabledSubmit={disabledSubmit}
            handleSubmit={() => handleStepSubmit(currentStep.id, surveyEngine)}
            currentStep={currentStep}
          />
        ) : (
          <Group justify="flex-end" gap="xs">
            {/* Back Button */}
            {currentStepIndex !== 0 ? (
              <ActionIcon
                mt="xl"
                variant="filled"
                radius="xl"
                onClick={goToPreviousStep}
                data-testid="survey-back-button"
              >
                <IconChevronUp />
              </ActionIcon>
            ) : null}
            {/* Next Button - Only show if required question is answered or question is not required */}
            {!disabledSubmit ? (
              <ActionIcon
                mt="xl"
                variant="filled"
                radius="xl"
                onClick={() => handleStepSubmit(currentStep.id, surveyEngine)}
              >
                <IconChevronDown />
              </ActionIcon>
            ) : null}
          </Group>
        )}
      </Container>
    </div>
  );
});
